import { type LinkRun, pairLinks } from "@ext/git/core/Diff/logic/linkChanges";
import t from "@ext/localization/locale/translate";
import type { JSONContent } from "@tiptap/core";
import type { Node, Schema } from "prosemirror-model";

export interface AttributeRow {
	label: string;
	from: string;
	to: string;
}

export interface AttributeRows {
	rows: AttributeRow[];
	removedLinks: { text: string; href: string }[];
	addedLinks: { text: string; href: string }[];
}

const nodesOf = (node: Node, type: string): Node[] => {
	const found: Node[] = [];
	if (node.type.name === type) found.push(node);
	node.descendants((child) => {
		if (child.type.name === type) found.push(child);
		return true;
	});
	return found;
};

const linkLabel = (run: LinkRun) =>
	run.text === run.href || run.text === run.target
		? t("diff.attrs.link-plain")
		: t("diff.attrs.link").replace("{text}", run.text);

const MIN_EDITED_LINK_SIMILARITY = 0.6;

const withoutScheme = (value: string) => value.replace(/^[a-z][a-z\d+.-]*:\/\//i, "");

const sharedEdges = (leftValue: string, rightValue: string) => {
	const left = withoutScheme(leftValue);
	const right = withoutScheme(rightValue);
	const limit = Math.min(left.length, right.length);
	let prefix = 0;
	while (prefix < limit && left[prefix] === right[prefix]) prefix++;
	let suffix = 0;
	while (suffix < limit - prefix && left[left.length - 1 - suffix] === right[right.length - 1 - suffix]) suffix++;
	return (prefix + suffix) / Math.max(left.length, right.length, 1);
};

const isEditedLink = (old: LinkRun, now: LinkRun) =>
	old.target === now.target ||
	sharedEdges(old.text, now.text) >= MIN_EDITED_LINK_SIMILARITY ||
	sharedEdges(old.target, now.target) >= MIN_EDITED_LINK_SIMILARITY;

const pairEditedLinks = (removed: LinkRun[], added: LinkRun[]) => {
	const used = new Set<number>();
	const pairs: { old: LinkRun; now: LinkRun }[] = [];
	const unpaired: LinkRun[] = [];
	for (const now of added) {
		const index = removed.findIndex((old, position) => !used.has(position) && isEditedLink(old, now));
		if (index < 0) {
			unpaired.push(now);
			continue;
		}
		used.add(index);
		pairs.push({ old: removed[index], now });
	}
	return { pairs, removed: removed.filter((_, position) => !used.has(position)), added: unpaired };
};

const fileName = (src: unknown) =>
	String(src ?? "")
		.split("/")
		.pop() ?? "";

export const attributeRows = (oldContent: JSONContent, newContent: JSONContent, schema: Schema): AttributeRows => {
	const before = schema.nodeFromJSON(oldContent);
	const after = schema.nodeFromJSON(newContent);
	const rows: AttributeRow[] = [];

	// Pair by order only when counts match, or removing one image shows a fake "a.png → b.png" swap.
	const alignedPairs = (type: string): [Node, Node][] => {
		const oldNodes = nodesOf(before, type);
		const newNodes = nodesOf(after, type);
		if (oldNodes.length !== newNodes.length) return [];
		return oldNodes.map((node, index) => [node, newNodes[index]]);
	};

	for (const [image, pair] of alignedPairs("image")) {
		if (image.attrs.src !== pair.attrs.src) {
			rows.push({ label: t("diff.attrs.image"), from: fileName(image.attrs.src), to: fileName(pair.attrs.src) });
		}
	}

	for (const [heading, pair] of alignedPairs("heading")) {
		if (heading.attrs.level !== pair.attrs.level) {
			rows.push({
				label: t("diff.attrs.heading"),
				from: t("diff.attrs.level").replace("{level}", String(heading.attrs.level)),
				to: t("diff.attrs.level").replace("{level}", String(pair.attrs.level)),
			});
		}
	}

	const languageOf = (node: Node) => (node.attrs.language as string | null) || t("diff.attrs.not-set");
	for (const [block, pair] of alignedPairs("code_block")) {
		if ((block.attrs.language ?? null) !== (pair.attrs.language ?? null)) {
			rows.push({ label: t("diff.attrs.code-language"), from: languageOf(block), to: languageOf(pair) });
		}
	}

	const links = pairLinks(before, after);
	for (const { run, old } of links.changed) {
		rows.push({ label: linkLabel(run), from: old.target, to: run.target });
	}

	const edited = pairEditedLinks(links.removed, links.added);
	for (const { old, now } of edited.pairs) {
		if (old.target !== now.target) rows.push({ label: linkLabel(now), from: old.target, to: now.target });
	}

	return {
		rows,
		removedLinks: edited.removed.map(({ text, target }) => ({ text, href: target })),
		addedLinks: edited.added.map(({ text, target }) => ({ text, href: target })),
	};
};

export default attributeRows;
